// What this order's customer has actually been sent.
//
// Sending is spread across a lot of triggers -- an invoice, a status
// change, a receipt, a manual send -- and each one either worked or
// quietly didn't. Rather than trusting that, or hunting through an inbox,
// the order records every attempt and this shows them.
//
// Collapsed by default: it answers a question you only sometimes have, and
// the order screen is long enough already.
import React from 'react';
import { CheckCircle2, ChevronDown, XCircle } from 'lucide-react';
import type { EmailLogEntry, OrderFulfillment, OrderStatus } from '../../types';
import { STATUS_LABEL } from '../../pages/admin/orderUtils';
import { statusLabel } from '../../utils/orderProgress';

// The template keys the backend records, in words. A status email is
// recorded as "status:<status>" so the log says which one went out.
const KIND_LABEL: Record<string, string> = {
  invoice: 'Invoice',
  receipt: 'Payment receipt',
  request_ack: 'Order received',
  status: 'Status update',
  admin_only: 'Customer email',
};

const REASON_LABEL: Record<string, string> = {
  suppressed_for_order: "Not sent — emails are off for this order",
  ses_not_configured: 'Not sent — email was not configured yet',
  no_recipient: 'Not sent — no customer email address',
  no_email_address: 'Not sent — no customer email address',
};

const describeKind = (kind: string, fulfillment?: OrderFulfillment): string => {
  if (kind.startsWith('status:')) {
    const status = kind.slice('status:'.length) as OrderStatus;
    return `Status update — ${statusLabel(STATUS_LABEL, status, fulfillment)}`;
  }
  return KIND_LABEL[kind] ?? kind;
};

const when = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const describeReason = (reason?: string): string => {
  if (!reason) return 'Not sent';
  return REASON_LABEL[reason] ?? `Not sent — ${reason}`;
};

const EmailLog: React.FC<{ entries?: EmailLogEntry[]; fulfillment?: OrderFulfillment }> = ({
  entries,
  fulfillment,
}) => {
  // Newest first: the last thing sent is what you're usually checking.
  const rows = [...(entries ?? [])].reverse();

  return (
    <details className="bg-white border border-background-dark rounded group">
      <summary className="flex items-center gap-2 p-4 cursor-pointer list-none">
        <ChevronDown
          size={16}
          className="text-text-light transition group-open:rotate-180"
        />
        <span className="text-sm font-medium">Emails sent</span>
        <span className="text-xs text-text-light">
          {rows.length === 0
            ? 'none yet'
            : `${rows.length} · last ${when(rows[0].at)}`}
        </span>
      </summary>

      <div className="border-t border-background-dark px-4 py-3">
        {rows.length === 0 ? (
          <p className="text-sm text-text-light">
            Nothing has been emailed to this customer yet. Sending an invoice
            or changing the status will show up here.
          </p>
        ) : (
          <ul className="space-y-3">
            {rows.map((entry, i) => (
              <li key={`${entry.at}-${i}`} className="flex items-start gap-2 text-sm">
                {entry.sent ? (
                  <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-green-600" />
                ) : (
                  <XCircle size={15} className="mt-0.5 shrink-0 text-text-light" />
                )}
                <div className="min-w-0">
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <span className="font-medium">
                      {describeKind(entry.kind, fulfillment)}
                    </span>
                    <span className="text-xs text-text-light">{when(entry.at)}</span>
                  </div>
                  {entry.subject && (
                    <div className="text-xs text-text-light truncate">
                      {entry.subject}
                    </div>
                  )}
                  <div className="text-xs text-text-light">
                    {entry.sent ? `To ${entry.to}` : describeReason(entry.reason)}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </details>
  );
};

export default EmailLog;
